import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";
import { buildQueryString } from "@/shared/utils/buildQueryString";

import { CARDS_API_ENDPOINTS } from "../constants/cards.constants";
import type { CardTransaction, CardTransactionsResponse } from "../types/card-movements.types";
import type { CardStatementParams, CardStatementResponse } from "../types/card-statement.types";
import type { CardStatsRange, CardStatsResponse } from "../types/card-stats.types";
import type {
  CardsApiResponse,
  CardsCountsResponse,
  CardsFilters,
  Card,
  CardViewUrlResponse,
  CardTopupRequest,
  CardTopupResponse,
  TransferBetweenCardsResponse,
  TransferBetweenCardsRequest,
  CardDetail,
  CardBalanceResponse,
  CardMaintenanceStatusResponse,
  SendCardDataEmailResponse,
  CardDepositAddress,
  CardTransferTarget,
  CardTransferTargetsResponse,
} from "../types/cards-api.types";

export type CardTransactionsParams = {
  skip?: number;
  limit?: number;
};

export class CardsService {
  static async fetchCards(filters: CardsFilters): Promise<AxiosResponse<CardsApiResponse>> {
    const url = buildQueryString(CARDS_API_ENDPOINTS.CARDS, filters);

    return HttpApiService.get<CardsApiResponse>(url);
  }

  static async fetchCardsCounts(): Promise<AxiosResponse<CardsCountsResponse>> {
    return HttpApiService.get<CardsCountsResponse>(`${CARDS_API_ENDPOINTS.CARDS}/counts`);
  }

  static async fetchCardStats(
    cardId: string,
    range: CardStatsRange,
  ): Promise<AxiosResponse<CardStatsResponse>> {
    return HttpApiService.get<CardStatsResponse>(
      `${CARDS_API_ENDPOINTS.CARDS}/${cardId}/stats?range=${range}`,
    );
  }

  static async createCard(
    params: {
      identityId: string;
      alias?: string;
      pocketId?: string;
    },
    opts?: { twoFactorCode?: string },
  ): Promise<AxiosResponse<Card>> {
    const body: Record<string, string> = { identity_id: params.identityId };
    if (params.alias?.trim()) body.alias = params.alias.trim();
    if (params.pocketId) body.pocket_id = params.pocketId;
    const headers = opts?.twoFactorCode ? { "x-two-factor-code": opts.twoFactorCode } : undefined;
    return HttpApiService.post<Card>(CARDS_API_ENDPOINTS.CARDS, body, headers ? { headers } : undefined);
  }

  static async getCardById(id: string): Promise<AxiosResponse<CardDetail>> {
    return HttpApiService.get<CardDetail>(`${CARDS_API_ENDPOINTS.CARDS}/${id}`);
  }

  static async getCardTransactions(
    id: string,
    params?: CardTransactionsParams,
  ): Promise<AxiosResponse<CardTransactionsResponse>> {
    const path = `${CARDS_API_ENDPOINTS.CARDS}/${id}/transactions`;
    const queryParams: string[] = [];
    if (params?.skip != null) queryParams.push(`skip=${params.skip}`);
    if (params?.limit != null) queryParams.push(`limit=${params.limit}`);
    const url = queryParams.length ? `${path}?${queryParams.join("&")}` : path;
    return HttpApiService.get<CardTransactionsResponse>(url);
  }

  static async getCardStatement(
    cardId: string,
    params: CardStatementParams,
  ): Promise<AxiosResponse<CardStatementResponse>> {
    const url = buildQueryString(`${CARDS_API_ENDPOINTS.CARDS}/${cardId}/statement`, params);
    return HttpApiService.get<CardStatementResponse>(url);
  }

  static async getCardTransactionDetail(
    cardId: string,
    transactionId: string,
  ): Promise<AxiosResponse<CardTransaction>> {
    return HttpApiService.get<CardTransaction>(
      `${CARDS_API_ENDPOINTS.CARDS}/${cardId}/transactions/${transactionId}`,
    );
  }

  static async generateCardViewUrl(
    id: string,
    opts?: { twoFactorCode?: string },
  ): Promise<AxiosResponse<CardViewUrlResponse>> {
    const endpoint = `${CARDS_API_ENDPOINTS.CARDS}/${id}/generate-view-url`;
    const headers = opts?.twoFactorCode ? { "x-two-factor-code": opts.twoFactorCode } : undefined;
    return HttpApiService.post<CardViewUrlResponse>(endpoint, {}, headers ? { headers } : undefined);
  }

  static async sendCardDataEmail(
    id: string,
    opts?: { twoFactorCode?: string },
  ): Promise<AxiosResponse<SendCardDataEmailResponse>> {
    const endpoint = `${CARDS_API_ENDPOINTS.CARDS}/${id}/send-data-email`;
    const headers = opts?.twoFactorCode ? { "x-two-factor-code": opts.twoFactorCode } : undefined;
    return HttpApiService.post<SendCardDataEmailResponse>(endpoint, {}, headers ? { headers } : undefined);
  }

  static async updateCardStatus(
    id: string,
    status: string,
    opts?: { twoFactorCode?: string },
  ): Promise<AxiosResponse<Card>> {
    const headers = opts?.twoFactorCode ? { "x-two-factor-code": opts.twoFactorCode } : undefined;
    return HttpApiService.patch<Card>(
      `${CARDS_API_ENDPOINTS.CARDS}/${id}/status`,
      { status },
      headers ? { headers } : undefined,
    );
  }

  static async retryCardIssuance(id: string): Promise<AxiosResponse<Card>> {
    return HttpApiService.post<Card>(`${CARDS_API_ENDPOINTS.CARDS}/${id}/retry-provider`, {});
  }

  static async deleteCard(id: string): Promise<AxiosResponse<void>> {
    return HttpApiService.delete<void>(`${CARDS_API_ENDPOINTS.CARDS}/${id}`);
  }

  static async updateCardAlias(id: string, alias: string): Promise<AxiosResponse<Card>> {
    return HttpApiService.patch<Card>(`${CARDS_API_ENDPOINTS.CARDS}/${id}/alias`, { alias });
  }

  static async getCardBalance(id: string): Promise<AxiosResponse<CardBalanceResponse>> {
    return HttpApiService.get<CardBalanceResponse>(`${CARDS_API_ENDPOINTS.CARDS}/${id}/balance`);
  }

  /**
   * The on-chain addresses that fund the card. `[]` when the card has none (not every issuer integration
   * supports crypto deposits); 409 while the card is not ACTIVE. Unwrapped here because the body IS the
   * list.
   */
  static async getCardDepositAddresses(id: string): Promise<CardDepositAddress[]> {
    const response = await HttpApiService.get<CardDepositAddress[]>(
      `${CARDS_API_ENDPOINTS.CARDS}/${id}/deposit-addresses`,
    );
    return response.data;
  }

  static async topupCard(
    topupData: CardTopupRequest,
    opts?: { twoFactorCode?: string },
  ): Promise<AxiosResponse<CardTopupResponse>> {
    const endpoint = `${CARDS_API_ENDPOINTS.CARDS}/topup`;
    const headers = opts?.twoFactorCode ? { "x-two-factor-code": opts.twoFactorCode } : undefined;
    return HttpApiService.post<CardTopupResponse>(endpoint, topupData, headers ? { headers } : undefined);
  }

  static async transfer(
    transferData: TransferBetweenCardsRequest,
    opts?: { twoFactorCode?: string },
  ): Promise<AxiosResponse<TransferBetweenCardsResponse>> {
    const endpoint = `${CARDS_API_ENDPOINTS.CARDS}/transfer`;
    const headers = opts?.twoFactorCode ? { "x-two-factor-code": opts.twoFactorCode } : undefined;
    return HttpApiService.post<TransferBetweenCardsResponse>(
      endpoint,
      transferData,
      headers ? { headers } : undefined,
    );
  }

  static async fetchTransferTargets(cardId: string): Promise<CardTransferTarget[]> {
    try {
      const response = await HttpApiService.get<CardTransferTargetsResponse>(
        `${CARDS_API_ENDPOINTS.CARDS}/${cardId}/transfer-targets`,
      );
      return response.data.data;
    } catch (err: unknown) {
      // A backend without this route yet: list the organization's cards as before.
      if (!HttpApiService.isAxiosError(err) || err.response?.status !== 404) throw err;
      const response = await CardsService.fetchCards({
        limit: 100,
        skip: 0,
        status: "ACTIVE",
        no_wallet: true,
      });
      const cards = Array.isArray(response.data?.data) ? response.data.data : [];
      return cards
        .filter((c) => c.id !== cardId)
        .map((c) => ({
          id: c.id,
          alias: c.alias ?? null,
          last_4: c.last_4 ?? c.last4 ?? "",
          card_holder_name: c.card_holder_name ?? c.name ?? null,
        }));
    }
  }

  static async getMaintenanceStatus(
    organizationId?: string,
  ): Promise<AxiosResponse<CardMaintenanceStatusResponse>> {
    return HttpApiService.get<CardMaintenanceStatusResponse>(
      `${CARDS_API_ENDPOINTS.CARDS}/maintenance-status`,
      organizationId ? { params: { organization_id: organizationId } } : undefined,
    );
  }
}
